import { memoizeOne } from "~/EasyProjectsCommon/memoizeOne";

export interface ColorRGB {
    r: number;
    g: number;
    b: number;
}

export interface ColorRGBA extends ColorRGB {
    alpha: number;
}

export const RGBtoRGBA = (color: ColorRGB, alpha: number = 1): ColorRGBA => ({
    r: color.r,
    g: color.g,
    b: color.b,
    alpha,
});

export const convertRGBAtoRGB = (color: ColorRGBA, bgColor: ColorRGB = { r: 255, g: 255, b: 255 }): ColorRGB => ({
    r: (1 - color.alpha) * bgColor.r + color.alpha * color.r,
    g: (1 - color.alpha) * bgColor.g + color.alpha * color.g,
    b: (1 - color.alpha) * bgColor.b + color.alpha * color.b,
});

const lerp = (start: number, end: number, amount: number) => {
    const difference = end - start;
    const adjusted = difference * amount;

    return start + adjusted;
};

export const hexToRgb = memoizeOne((hex: string): ColorRGB => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
});

export const colorRGB = ({ r, g, b }: ColorRGB) => `rgb(${r}, ${g}, ${b})`;
export const colorRGBA = ({ r, g, b }: ColorRGB, opacity = 1) => `rgba(${r}, ${g}, ${b}, ${opacity})`;

// The lightness ranges from 0.0 through 1.0, where 0.0 represents black and 1.0 represents white.
export const getBrightness = ({ r, g, b }: ColorRGB) => {
    const maxValue = 255;
    const num1 = r / maxValue;
    const num2 = g / maxValue;
    const num3 = b / maxValue;
    let num4 = num1;
    let num5 = num1;

    if (num2 > num4) {
        num4 = num2;
    }

    if (num3 > num4) {
        num4 = num3;
    }

    if (num2 < num5) {
        num5 = num2;
    }

    if (num3 < num5) {
        num5 = num3;
    }

    return (num4 + num5) / 2.0;
};

export const colorBlending = (color: ColorRGB, to: ColorRGB, amount: number): ColorRGB => {
    const r = Math.round(lerp(color.r, to.r, amount));
    const g = Math.round(lerp(color.g, to.g, amount));
    const b = Math.round(lerp(color.b, to.b, amount));

    return {
        r,
        g,
        b,
    };
};

export const getRGBAfromHex = (hex: string, opacity?: number) => {
    const rgb: ColorRGB = hexToRgb(hex);
    const { r, g, b } = rgb;

    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
};

export const contrastColors = {
    black: "black",
    white: "white",
};

export const getContrast = (hex: string) => {
    const { r, g, b }: ColorRGB = hexToRgb(hex);
    return (Math.round(r * 299) + Math.round(g * 587) + Math.round(b * 114)) / 1000;
}; // https://24ways.org/2010/calculating-color-contrast

export const getContrastTextColor = (hex: string) => {
    if (!hex) {
        return;
    }

    const contrast: number = getContrast(hex);

    return contrast >= 128 ? contrastColors.black : contrastColors.white; // 128 it`s 255/2
};

export const rgb2hex = (rgbString: string) => {
    if (rgbString == null || rgbString.search("rgb") === -1) {
        return rgbString;
    }
    const rgb = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+))?\)$/.exec(rgbString);
    const hex = (x: string) => ("0" + parseInt(x, 10).toString(16)).slice(-2);
    return "#" + hex(rgb[1]) + hex(rgb[2]) + hex(rgb[3]);
};

export const isLowBrightness = (color: ColorRGB) => getBrightness(color) <= 0.5;
